/* eslint-disable no-nested-ternary */
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PostPsTreeSearchParams, PsTreeItem, PsTreeSearchConditions } from '../../../../../api/model';
import { usePostPsTreeSearch, usePostPsTreeSearchList } from '../../../../../api/ps-trees/ps-trees';
import { getAxiosRequestConfig } from '../../../../../utils/api';
import ErrorComponent from '../../../../atoms/ErrorComponent';
import PsTree, { PsTreePsTreeGetterCommonProps, getPsTreeItem } from './PsTree';

interface Props extends PsTreePsTreeGetterCommonProps {
  /** postPsTreeSearch に渡す、PS ツリーの絞り込み条件。 */
  psTreeSearchConditions: PsTreeSearchConditions;

  /** PS ツリーを再検索 (postPsTreeSearch) するか否か。 */
  doRePostPsTreeSearch: boolean;
  /** PS ツリーを再検索 (postPsTreeSearch) するか設定する関数。 */
  setDoRePostPsTreeSearch: (doRePostPsTreeSearch: boolean) => void;

  /** PS 出力・原作用 PS 出力用に、出力対象となる、現時点での PS ツリーの search_result = true となっている PS の バージョン ID の配列を設定する関数。 */
  setPsVersionIdsToDownload: (psVersionIdsToDownload: number[]) => void;

  /** PS出力可能かどうかを設定する関数 */
  setCanPsDownload: (canPsDownload: boolean) => void;
}

/** PS 比較画面以外の PS ツリー用データの取得コンポーネント。 */
function PsTreeGetter({
  mode,
  psTreeSearchConditions,
  doRePostPsTreeSearch,
  setDoRePostPsTreeSearch,
  setPsVersionIdsToDownload,
  selectedPsTreeItem,
  targetPs,
  selectedPsVersionId,
  setCanPsDownload,
  // selectedChildContentsPsVersionIds,
  // setSelectedPsVersionId,
  // setSelectedChildContentsPsVersionIds,
  // setModifiedRootPsTreeItem,
  ...props
}: Props) {

  // i18n 用。
  const {
    // t,
    i18n,
  } = useTranslation();

  /** PS ツリー取得 API で取得したデータを保持する state。ツリー項目展開時に段階的に取得するため、以前に取得していたデータを保持しておき、新規取得したデータとマージする必要がある。 */
  const [rootPsTreeItem, setRootPsTreeItem] = useState<PsTreeItem | undefined>(undefined);

  /** PS ツリー項目の子供展開ボタン (caret) が押された PS の ID。 */
  const [psIdToFetchDescendants, setPsIdToFetchDescendants] = useState<number | undefined>(undefined);
  /** psIdToFetchDescendants の一つ前の値。 */
  const [previousPsIdToFetchDescendants, setPreviousPsIdToFetchDescendants] = useState<number | undefined>(psIdToFetchDescendants);

  /** psIdToFetchDescendants が指す PsTreeItem。 */
  const psTreeItemToFetchDescendants =
    (
      (typeof rootPsTreeItem !== 'undefined')
      &&
      (Number.isInteger(psIdToFetchDescendants))
    ) ?
      getPsTreeItem(rootPsTreeItem, psIdToFetchDescendants as number) : undefined;

  // const psTreeItemToFetchDescendants = useMemo(
  //   () =>
  //     (
  //       (typeof rootPsTreeItem !== 'undefined')
  //       &&
  //       (Number.isInteger(psIdToFetchDescendants))
  //     ) ?
  //       getPsTreeItem(rootPsTreeItem, psIdToFetchDescendants as number) : undefined,
  //   [rootPsTreeItem, psIdToFetchDescendants]
  // );

  /** PS ツリーを編集 (ドラッグ & ドロップ) するか否か。 */
  const doEditPsTree = useMemo(
    () =>
    // ルート PS (L0 PS) および章 PS (L1 PS) の編集時のみ可能。
    (
      (mode === 'Edit')
      &&
      // PS 編集画面では PS が取得された後に targetPs が undefined → Ps になる。
      (Number(targetPs?.type) <= 1)
      // (Number(selectedPsTreeItem?.type) <= 1)
    ),
    [mode, targetPs?.type]
  );

  /** 過去の prop との比較用。 */
  const [previousPsTreeSearchConditions, setPreviousPsTreeSearchConditions] =
    useState<PsTreeSearchConditions>(psTreeSearchConditions);

  const axiosConfig = getAxiosRequestConfig(i18n.language);

  // PS ツリーのデータを取得する。
  const options = {
    // mutation: {
    //   // onSuccess: (
    //   //   data: AxiosResponse<PsTreeItem, unknown>
    //   //   // , variables: { data: PsTreeSearchConditions; },
    //   //   // context: any
    //   // ) => {
    //   //   // 返り値を state に保存。
    //   //   setPsTreeSearchResult(data.data);

    //   //   console.log('data: ', data);
    //   //   console.log('data.data: ', data.data);
    //   //   // console.log('variables: ', variables);
    //   //   // console.log('context: ', context);
    //   // },
    //   // onError: (error: any, variables: any, context: any) => {
    //   //   // An error happened!
    //   //   console.log('error: ', error);
    //   // },
    //   // 絞り込み条件。
    //   // data: (psTreeSearchConditions) ?? {},
    // },
    axios: axiosConfig,
  };

  // PS ツリー取得 API。
  const postPsTreeSearchMutation = usePostPsTreeSearch(options);

  // PSツリーに含まれるPSバージョンIDリスト取得API
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const postPsTreeSearchListMutation = usePostPsTreeSearchList(options);

  // console.log('postPsTreeSearchMutation: ', postPsTreeSearchMutation);

  // const isMutatingPostPsTreeSearch = useIsMutating({ mutationKey: ['/pstrees/search'] });
  // const isMutatingPostPsTreeSearch = useIsMutating();

  // console.log('isMutatingPostPsTreeSearch: ', isMutatingPostPsTreeSearch);

  /** 編集中ツリーを取得したか否かを記録。1 回だけ取得すればよい。 */
  const [didGetTempPsTree, setDidGetTempPsTree] = useState<boolean>(false);

  // console.log('targetPs in PsTree:', targetPs);
  // console.log('Number(targetPs?.type):', Number(targetPs?.type));

  /** postPsTreeSearchMutation.mutate() に渡す params。 */
  const postPsTreeSearchParams: PostPsTreeSearchParams = useMemo(
    () => {
      const params: PostPsTreeSearchParams = {};

      if (doEditPsTree) {
        // ルート PS (L0 PS) および章 PS (L1 PS) の PS 編集画面では、編集中ツリーを取得して表示する。
        params.edit_ps_id = Number(targetPs?.ps_id); // Number(selectedPsTreeItem?.ps_id);
      }
      else {
        // 取得ルート PS ID。
        // L0 の場合、L0 と L1 全て取得 (disp_ps_version_id で L2 以降が指定されている場合は L0 と L1 全て + 当該 PS を含む L1 の子孫全て)。
        // L1 の場合、その L1 と子孫全て。
        // 未指定時は L0 から全取得。
        // 基本的に 1 としておく。
        params.disp_root_ps_id = 1;

        if (Number.isInteger(selectedPsVersionId)) {
          // PS バージョン ID が指定されている場合。
          // URL で、詳細を表示する PS が指定されている → L2 以降の場合、PS ツリーも L0 と L1 全て + 当該 PS を含む L1 の子孫全てを取得。
          // disp_root_ps_id=1 (L0 PS) の時のみ、disp_ps_version_id で指定した PS を配下に含む L1 PS の子孫を全て取得する。
          // disp_root_ps_id がそれ以外の時は、このパラメーターは無視される。
          params.disp_ps_version_id = selectedPsVersionId;

        }

        if (Number.isInteger(psIdToFetchDescendants)) {
          // psIdToFetchDescendants が指定されていたら (現状では L1 PS ツリー項目の子供展開ボタン (caret) 押下時に子孫が未取得であった場合) 渡す。
          params.disp_root_ps_id = psIdToFetchDescendants;
        }
      }

      return params;
    },
    [doEditPsTree, targetPs?.ps_id, psIdToFetchDescendants, selectedPsVersionId]
  );

  // API を呼ぶ Effect。
  useEffect(
    () => {

      /** 段階的取得 (子孫の取得) が必要か否か。 */
      const doGetDescendants = (
        // (現状では L1 PS ツリー項目の) 子供展開ボタン (caret) が押下された。
        (Number.isInteger(psIdToFetchDescendants))
        &&
        // 値が変わっている。
        (psIdToFetchDescendants !== previousPsIdToFetchDescendants)
        &&
        (psTreeItemToFetchDescendants)
        &&
        // 展開ボタン (caret) が押下された PS (PS ツリー項目) の子孫がまだ取得されていない場合。
        // (この条件は取得後に子孫が存在しない場合でも満たされる。そのため無限ループ防止のため、previousPsIdToFetchDescendants と比較して値が変わっている場合のみという条件を付け足している。)
        (!(
          (Array.isArray(psTreeItemToFetchDescendants.child_tree_items))
          &&
          (psTreeItemToFetchDescendants.child_tree_items.length > 0)
        ))
      );

      // 更新。
      setPreviousPsIdToFetchDescendants(psIdToFetchDescendants);

      /** 編集中ツリーの取得が必要か否か。 */
      const doGetTempPsTree = (
        // params.edit_ps_id が指定されていて、
        (Number.isInteger(postPsTreeSearchParams.edit_ps_id))
        &&
        // 編集中ツリーがまだ取得されていない場合。
        (!didGetTempPsTree)
      );

      /** 編集中でない通常のツリーの取得が必要か否か。 */
      const doGetNormalPsTree = (
        (
          // 編集中ツリーを取得し表示していたが、
          (didGetTempPsTree)
          &&
          // 編集画面から他の画面に遷移した場合。
          (mode !== 'Edit')
        )
        ||
        // 段階的取得 (子孫の取得) が必要な場合。
        doGetDescendants
      );

      // console.log('didGetTempPsTree', didGetTempPsTree);
      // console.log('targetPs', targetPs);


      // console.log('(postPsTreeSearchMutation.isIdle):', (postPsTreeSearchMutation.isIdle));
      // console.log('(postPsTreeSearchMutation.isSuccess) && !_.isEqual():', (
      //   (postPsTreeSearchMutation.isSuccess)
      //   &&
      //   !_.isEqual(psTreeSearchConditions, previousPsTreeSearchConditions)
      // ));
      // console.log('(doRePostPsTreeSearch):', (doRePostPsTreeSearch));
      // console.log('(doGetTempPsTree):', (doGetTempPsTree));
      // console.log('(doGetNormalPsTree):', (doGetNormalPsTree));

      // PS ツリーを取得する。
      if (
        // Mutation では同時に発生するリクエストの重複は排除されない？
        // 取得が重複しないようにする。
        (!(postPsTreeSearchMutation.isLoading))
        &&
        (
          // TODO?: ↓ status の確認は不要？isIdle の時は previousPsTreeSearchConditions === undefined で psTreeSearchConditions と異なり、検索条件は変更されているということになるだろうし、isSuccess じゃなくても検索条件が変更されていれば常に実行、でもいいか。
          // ロード時に POST するため、isIdle で実行。
          // isIdle or status === 'idle' - The mutation is currently idle or in a fresh/reset state。
          (postPsTreeSearchMutation.isIdle)
          ||
          // また、その後検索条件が変更された時にも実行。
          (
            (postPsTreeSearchMutation.isSuccess)
            &&
            // TODO?: Lodash を使用しないことにする場合は
            // JSON.stringify(psTreeSearchConditions) !== JSON.stringify(previousPsTreeSearchConditions)
            // で比較するか？

            // 検索条件が変更された。
            !_.isEqual(psTreeSearchConditions, previousPsTreeSearchConditions)
          )
          ||
          // 検索条件が変更されていないが、再検索が必要な場合。
          (doRePostPsTreeSearch)
          ||
          // 編集中ツリーの取得が必要。
          (doGetTempPsTree)
          ||
          // 編集中でない通常のツリーの取得が必要。
          (doGetNormalPsTree)
        )
        &&
        // PS バージョン ID だけでは、type (L0 / L1 かそれら以外か) の判別がつかないので、PS が取得され、type が判明して初めて編集中ツリーが取得されることになる。
        // つまり targetPs が undefined のうちは取得しない。
        (
          !(
            (mode === 'Edit')
            &&
            (!targetPs)
          )
        )
      ) {
        // TODO: PsTree でリセットは必要？
        // // ドラッグ & ドロップ用データをリセット。
        // setSortableTreeItems([]);

        if (!doGetDescendants) {
          // 子孫を取得する場合はマージする。
          // それ以外ではリセット。
          setRootPsTreeItem(undefined);
        }

        // API 呼び出し。
        postPsTreeSearchMutation.mutate({
          // 絞り込み条件。
          data: psTreeSearchConditions ?? {},
          params: postPsTreeSearchParams,
        });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        postPsTreeSearchListMutation.mutate({
          data: psTreeSearchConditions ?? {},
        });

        // APIの結果が戻るまでPS出力リンクを無効化
        setCanPsDownload(false);

        // 検索条件を比較用に保存する。
        setPreviousPsTreeSearchConditions(psTreeSearchConditions);

        if (doRePostPsTreeSearch) {
          // 検索を実行したので成功 / 失敗に関わらず「再検索が必要」フラグを false にする。
          setDoRePostPsTreeSearch(false);
        }

        if (doGetTempPsTree) {
          // 編集中ツリーは 1 回だけ取得する。
          setDidGetTempPsTree(true);
        }
        else if (doGetNormalPsTree) {
          // 編集中ツリー取得済み state をリセット。
          // これにより、編集中でない通常のツリーの取得が編集画面以外への遷移後 1 回のみ実行されることになる。
          setDidGetTempPsTree(false);
        }
        // ↑ ただしこうすると、以前取得した PS ツリー データから変更が無くても L0 / L1 では編集画面と他の画面の遷移の度に postPsTreeSearch が実行されることになる。が、まぁ仕方ないか。

        // 開発中は 2 回 mutate を呼び出しているが、理由については useEffect についての
        // https://beta.reactjs.org/reference/react/useEffect#my-effect-runs-twice-when-the-component-mounts
        // を参照。
        // React Query は同時に発生するリクエストの重複を排除するらしい。同じデータをリクエストする状況でも 1 つのネットワーク リクエストのみとなるようだ。

        // console.log('useEffect call postPsTreeSearchMutation.mutate()');
        // console.log('isMutatingPostPsTreeSearch: ', isMutatingPostPsTreeSearch);
      }
    },
    [didGetTempPsTree, doRePostPsTreeSearch, mode, postPsTreeSearchMutation, postPsTreeSearchListMutation, postPsTreeSearchParams, previousPsIdToFetchDescendants, previousPsTreeSearchConditions, psIdToFetchDescendants, psTreeItemToFetchDescendants, psTreeSearchConditions, setDoRePostPsTreeSearch, targetPs, setPsVersionIdsToDownload, setCanPsDownload]
  );

  /** 無限ループ防止用に、Mutation の status を比較用に保存する。 */
  const [previousPostPsTreeSearchMutationStatus, setPreviousPostPsTreeSearchMutationStatus] = useState<string>('');

  useEffect(
    () => {
      if (
        // loading → success になった時のみ実行。
        (previousPostPsTreeSearchMutationStatus === 'loading')
        &&
        (postPsTreeSearchMutation.status === 'success')
        &&
        (postPsTreeSearchMutation.data?.data)
      ) {
        // const psVersionIdsToDownload: number[] = [];

        // // ダウンロード用に再帰処理。
        // traversePsTreeItemsForDownload(postPsTreeSearchMutation.data.data, psVersionIdsToDownload); 
        // const psVersionIdsToDownload: number[] = postPsTreeSearchListMutation.data?.data ?? [];

        // PsDetailsPage の state に保存。
        // setPsVersionIdsToDownload(psVersionIdsToDownload);

        // 取得したデータを state に保存。
        if (rootPsTreeItem) {
          // マージする。

          if (Number.isInteger(postPsTreeSearchMutation.data.data.ps_id)) {
            // クローン。
            const newRootPsTreeItem = JSON.parse(JSON.stringify(rootPsTreeItem)) as PsTreeItem;

            // 子孫を更新する対象となる PsTreeItem を検索。
            const psTreeItemToAddDescendants = getPsTreeItem(newRootPsTreeItem, postPsTreeSearchMutation.data.data.ps_id as number);

            if (psTreeItemToAddDescendants) {
              // 取得した子孫に更新。
              psTreeItemToAddDescendants.child_tree_items = postPsTreeSearchMutation.data.data.child_tree_items;

              // 子孫を追加したデータに更新。
              setRootPsTreeItem(newRootPsTreeItem);
            }
          }
        }
        else {
          // 丸ごと上書き。
          setRootPsTreeItem(postPsTreeSearchMutation.data.data);
        }

        // 段階的取得対象をリセット。
        setPsIdToFetchDescendants(undefined);
        setPreviousPsIdToFetchDescendants(undefined);
      }

      if (previousPostPsTreeSearchMutationStatus !== postPsTreeSearchMutation.status) {
        // 比較用に保存。
        setPreviousPostPsTreeSearchMutationStatus(postPsTreeSearchMutation.status);
      }
    },
    [postPsTreeSearchMutation.data?.data, postPsTreeSearchListMutation.data?.data, postPsTreeSearchMutation.status, previousPostPsTreeSearchMutationStatus, rootPsTreeItem, setPsVersionIdsToDownload]
  );

  /** 無限ループ防止用に、Mutation の status を比較用に保存する。 */
  const [previousPostPsTreeSearchListMutationStatus, setPreviousPostPsTreeSearchListMutationStatus] = useState<string>('');

  useEffect(
    () => {
      if (
        // loading → success になった時のみ実行。
        (previousPostPsTreeSearchListMutationStatus === 'loading')
        &&
        (postPsTreeSearchListMutation.status === 'success')
        &&
        (postPsTreeSearchListMutation.data?.data)
      ) {
        // // ダウンロード用に再帰処理。
        const psVersionIdsToDownload: number[] = postPsTreeSearchListMutation.data?.data ?? [];

        // PsDetailsPage の state に保存。
        setPsVersionIdsToDownload(psVersionIdsToDownload);
        // PS出力リンクを有効化
        setCanPsDownload(true);
      }

      if (previousPostPsTreeSearchListMutationStatus !== postPsTreeSearchListMutation.status) {
        // 比較用に保存。
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        setPreviousPostPsTreeSearchListMutationStatus(postPsTreeSearchListMutation.status);
      }
    },
    [postPsTreeSearchListMutation.data?.data, postPsTreeSearchListMutation.status, previousPostPsTreeSearchListMutationStatus, setPsVersionIdsToDownload, setCanPsDownload]
  );

  if (postPsTreeSearchMutation.isError) {
    return (
      <div id="product-side-list">
        <ErrorComponent error={postPsTreeSearchMutation.error} />
      </div>
    );
  }

  if (postPsTreeSearchListMutation.isError) {
    return (
      <div id="product-side-list">
        <ErrorComponent error={postPsTreeSearchListMutation.error} />
      </div>
    );
  }

  // console.log('postPsTreeSearchMutation.data?.data', postPsTreeSearchMutation.data?.data);

  return (
    <PsTree
      mode={mode}
      rootPsTreeItem={rootPsTreeItem}
      selectedPsVersionId={selectedPsVersionId}
      selectedPsTreeItem={selectedPsTreeItem}
      targetPs={targetPs}
      setPsIdToFetchDescendants={setPsIdToFetchDescendants}
      {...props}
    />
  );
}

export default PsTreeGetter;

/** PS 出力・原作用 PS 出力用に、search_result = true となっている PS を探し、配列に追加する関数。 */
/* const traversePsTreeItemsForDownload = (
  psTreeItem: PsTreeItem,
  psVersionIdsToDownload: number[]
): void => {
  if (psTreeItem.search_result) {
    if (typeof psTreeItem.ps_version_id === 'number') {
      psVersionIdsToDownload.push(psTreeItem.ps_version_id);
    }
  }

  if (Array.isArray(psTreeItem.child_tree_items)) {
    psTreeItem.child_tree_items.forEach(
      (childPsTreeItem: PsTreeItem) => {
        // 再帰。
        traversePsTreeItemsForDownload(childPsTreeItem, psVersionIdsToDownload);
      }
    )
  }
}; */